import React from "react";
import {useQuery} from "@tanstack/react-query";
import {ShieldCheck, ShieldAlert, ShieldQuestion} from "lucide-react";
import {api} from "../../config/api";
import secureStorage from "../../utils/secureStorage";
import {SettingsTable, ListLoader} from "./SettingsUI";

const STATUS_LABELS = {
    active: "Active",
    expired: "Expirée",
    demo: "Version d'essai",
    demo_expired: "Essai expiré",
    no_license: "Aucune licence",
};

const formatDate = (isoDate) => {
    if (!isoDate) return "—";
    return new Date(isoDate).toLocaleDateString("fr-FR", {
        day: "2-digit",
        month: "long",
        year: "numeric",
    });
};

const formatPrice = (price) =>
    new Intl.NumberFormat("fr-MA", {style: "currency", currency: "MAD"}).format(price || 0);

const StatusBadge = ({status}) => {
    const styles = {
        active: {className: "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400", Icon: ShieldCheck},
        demo: {className: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400", Icon: ShieldQuestion},
        expired: {className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", Icon: ShieldAlert},
        demo_expired: {className: "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400", Icon: ShieldAlert},
        no_license: {className: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300", Icon: ShieldQuestion},
    };
    const {className, Icon} = styles[status] || styles.no_license;

    return (
        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${className}`}>
            <Icon size={14}/>
            {STATUS_LABELS[status] || status}
        </span>
    );
};

const StripCell = ({label, className = "text-gray-900 dark:text-gray-100", children}) => (
    <div className="h-16 flex flex-col justify-center px-4 sm:px-6">
        <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">{label}</span>
        <span className={`text-sm font-semibold tabular-nums ${className}`}>{children}</span>
    </div>
);

const fetchLicenseStatus = async () => {
    const account = await secureStorage.getItem("selectedAccount");
    const response = await api.get("/license/status", {
        headers: {"X-account-id": account.id},
    });
    return response.data;
};

const fetchLicenseHistory = async () => {
    const account = await secureStorage.getItem("selectedAccount");
    const response = await api.get("/license/list", {
        headers: {"X-account-id": account.id},
    });
    return response.data.licenses || [];
};

const LicenseSettings = () => {
    const {data: status, isLoading: statusLoading, error: statusError} = useQuery({
        queryKey: ["license-status"],
        queryFn: fetchLicenseStatus,
    });

    const {data: history = [], isLoading: historyLoading, error: historyError} = useQuery({
        queryKey: ["license-history"],
        queryFn: fetchLicenseHistory,
    });

    const daysRemaining = status?.license_end_date
        ? Math.ceil(
            (new Date(status.license_end_date).getTime() - new Date(status.server_time).getTime()) /
            (1000 * 60 * 60 * 24)
        )
        : null;

    const expired = status && (status.status === "expired" || status.status === "demo_expired");

    const historyColumns = [
        {key: "start", label: "Début", render: (licence) => formatDate(licence.start_date)},
        {key: "end", label: "Fin", render: (licence) => formatDate(licence.end_date)},
        {key: "status", label: "Statut", render: (licence) => <StatusBadge status={licence.status ? "active" : "expired"}/>},
        {key: "price", label: "Montant", className: "tabular-nums", render: (licence) => formatPrice(licence.total_price)},
    ];

    return (
        <div>
            {/* ── Statut ── */}
            {statusLoading ? (
                <ListLoader label="Chargement de la licence..."/>
            ) : statusError ? (
                <p className="p-4 text-center text-red-500">
                    Erreur : {statusError.response?.data?.detail || statusError.message}
                </p>
            ) : status && (
                <>
                    {/* 64px cells: same height as the section title bar and the sidebar rows */}
                    <div className="grid grid-cols-1 sm:grid-cols-3 border-b border-gray-200 dark:border-gray-700 divide-y sm:divide-y-0 sm:divide-x divide-gray-200 dark:divide-gray-700">
                        <StripCell label="Statut">
                            <span className="flex items-center gap-2">
                                <StatusBadge status={status.status}/>
                                {!status.authorized && (
                                    <span className="text-xs text-red-600 dark:text-red-400">Accès non autorisé</span>
                                )}
                            </span>
                        </StripCell>
                        <StripCell label="Expiration">{formatDate(status.license_end_date)}</StripCell>
                        <StripCell
                            label={daysRemaining !== null && daysRemaining < 0 ? "Expirée depuis" : "Jours restants"}
                            className={daysRemaining !== null && daysRemaining < 0 ? "text-red-600 dark:text-red-400" : undefined}
                        >
                            {daysRemaining === null ? "—" : `${Math.abs(daysRemaining)} j`}
                        </StripCell>
                    </div>
                    {expired && (
                        <p className="px-4 sm:px-6 py-3 text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                            Une période de grâce de {status.grace_period_days} jours peut s'appliquer.
                            Contactez votre fournisseur pour renouveler votre licence.
                        </p>
                    )}
                </>
            )}

            {/* ── Historique ── */}
            <h3 className="px-4 sm:px-6 pt-5 pb-3 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                Historique des licences
            </h3>
            {historyLoading ? (
                <ListLoader label="Chargement de l'historique..."/>
            ) : historyError ? (
                <p className="p-4 text-center text-red-500">
                    Erreur : {historyError.response?.data?.detail || historyError.message}
                </p>
            ) : (
                <SettingsTable
                    columns={historyColumns}
                    rows={history}
                    empty={{icon: ShieldQuestion, title: "Aucune licence enregistrée"}}
                />
            )}
        </div>
    );
};

export default LicenseSettings;
